import React, { Component } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./VendorPresenceMap.css";

const IRELAND_CENTER = [53.35, -8.0];
const IRELAND_BOUNDS = [
  [51.2, -11.1],
  [55.7, -5.2],
];
const COUNTY_GEOJSON_URL =
  "https://gist.githubusercontent.com/martinbpeters/34e258dadca967393291b7a128857350/raw/a246b15c25c66532842dad71e3a86276ee44055f/counties.geojson";

export default class VendorPresenceMap extends Component {
  constructor(props) {
    super(props);

    this.mapContainer = React.createRef();
    this.map = null;
    this.vendorLayers = null;
    this.countyLayer = null;

    this.state = {
      radiusKm: String(props.defaultRadiusKm || 5),
      countyLoading: true,
      countyError: false,
    };

    this._handleRadiusChange = this._handleRadiusChange.bind(this);
  }

  componentDidMount() {
    this.map = L.map(this.mapContainer.current, {
      center: IRELAND_CENTER,
      zoom: 7,
      minZoom: 6,
      maxBounds: IRELAND_BOUNDS,
      maxBoundsViscosity: 0.75,
      preferCanvas: true,
    });

    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      maxZoom: 19,
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    }).addTo(this.map);

    this.vendorLayers = L.layerGroup().addTo(this.map);
    this._loadCounties();
    this._drawLocations();
  }

  componentDidUpdate(previousProps, previousState) {
    if (
      previousProps.locations !== this.props.locations ||
      previousState.radiusKm !== this.state.radiusKm
    ) {
      this._drawLocations();
    }
  }

  componentWillUnmount() {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  _validLocations() {
    return (this.props.locations || []).filter((location) => {
      const latitude = Number(location.latitude);
      const longitude = Number(location.longitude);

      return (
        Number.isFinite(latitude) &&
        Number.isFinite(longitude) &&
        latitude >= IRELAND_BOUNDS[0][0] &&
        latitude <= IRELAND_BOUNDS[1][0] &&
        longitude >= IRELAND_BOUNDS[0][1] &&
        longitude <= IRELAND_BOUNDS[1][1]
      );
    });
  }

  _getCountyName(feature) {
    const properties = feature?.properties || {};

    return (
      properties.NAME_EN ||
      properties.ENGLISH ||
      properties.ENGLISH_NAME ||
      properties.COUNTY ||
      properties.County ||
      properties.county ||
      properties.NAME ||
      properties.name ||
      properties.NAME_2 ||
      "County"
    );
  }

  async _loadCounties() {
    try {
      const response = await fetch(
        this.props.countyGeoJsonUrl || COUNTY_GEOJSON_URL
      );

      if (!response.ok) {
        throw new Error("County boundary request failed.");
      }

      const countyGeoJson = await response.json();

      if (!this.map) {
        return;
      }

      if (this.countyLayer) {
        this.countyLayer.remove();
      }

      this.countyLayer = L.geoJSON(countyGeoJson, {
        style: {
          color: "#6c757d",
          weight: 1.25,
          opacity: 0.8,
          fillColor: "#f4f6f9",
          fillOpacity: 0.08,
        },
        onEachFeature: (feature, layer) => {
          const countyName = this._getCountyName(feature);

          layer.bindTooltip(countyName, {
            permanent: true,
            direction: "center",
            className: "vendor-map-county-label",
          });

          const countyPopup = document.createElement("strong");
          countyPopup.textContent = countyName;
          layer.bindPopup(countyPopup);
        },
      }).addTo(this.map);

      this.countyLayer.bringToBack();
      this.setState({ countyLoading: false, countyError: false });
    } catch (error) {
      console.error("Unable to load Ireland county boundaries:", error);

      if (this.map) {
        this.setState({ countyLoading: false, countyError: true });
      }
    }
  }

  _handleRadiusChange(event) {
    const value = event.target.value;

    if (value === "" || /^\d{0,3}(\.\d{0,2})?$/.test(value)) {
      this.setState({ radiusKm: value });
    }
  }

  _appliedRadiusKm() {
    const radiusKm = Number(this.state.radiusKm);

    return Number.isFinite(radiusKm) && radiusKm > 0
      ? Math.min(radiusKm, 200)
      : 5;
  }

  _createPopup(location, radiusKm) {
    const container = document.createElement("div");
    const heading = document.createElement("strong");
    const county = document.createElement("div");
    const radius = document.createElement("div");

    heading.textContent = location.name || "Vendor";
    county.textContent = `County: ${location.county || "Not provided"}`;
    radius.textContent = `Delivery radius: ${radiusKm} km`;

    container.appendChild(heading);
    container.appendChild(county);
    container.appendChild(radius);

    return container;
  }

  _drawLocations() {
    if (!this.map || !this.vendorLayers) {
      return;
    }

    this.vendorLayers.clearLayers();

    const locations = this._validLocations();
    const bounds = L.latLngBounds([]);
    const appliedRadiusKm = this._appliedRadiusKm();

    locations.forEach((location) => {
      const latitude = Number(location.latitude);
      const longitude = Number(location.longitude);
      const point = [latitude, longitude];
      const popup = this._createPopup(location, appliedRadiusKm);

      if (appliedRadiusKm > 0) {
        const deliveryArea = L.circle(point, {
          radius: appliedRadiusKm * 1000,
          color: "#ff5e8a",
          weight: 2,
          fillColor: "#ff5e8a",
          fillOpacity: 0.14,
        });

        deliveryArea.bindPopup(popup).addTo(this.vendorLayers);
        bounds.extend(deliveryArea.getBounds());
      }

      L.circleMarker(point, {
        radius: 7,
        color: "#ffffff",
        weight: 2,
        fillColor: "#ff5e8a",
        fillOpacity: 1,
      })
        .bindTooltip(location.name || "Vendor", {
          direction: "top",
          offset: [0, -8],
        })
        .bindPopup(this._createPopup(location, appliedRadiusKm))
        .addTo(this.vendorLayers);

      bounds.extend(point);
    });

    if (bounds.isValid()) {
      this.map.fitBounds(bounds, {
        padding: [35, 35],
        maxZoom: 10,
      });
    } else {
      this.map.setView(IRELAND_CENTER, 7);
    }
  }

  render() {
    const validCount = this._validLocations().length;
    const { radiusKm, countyLoading, countyError } = this.state;

    return (
      <div className="card card-custom mb-5">
        <div className="card-header">
          <div className="card-title">
            <h3 className="card-label">
              Vendor Presence and Delivery Coverage
              <small className="text-muted ml-2">
                {validCount} mapped location{validCount === 1 ? "" : "s"}
              </small>
            </h3>
          </div>

          <div className="card-toolbar">
            <div className="d-flex align-items-center">
              <label
                htmlFor="vendor-delivery-radius"
                className="font-weight-bold mb-0 mr-3"
              >
                Delivery radius
              </label>
              <div className="input-group" style={{ width: 150 }}>
                <input
                  id="vendor-delivery-radius"
                  type="number"
                  min="0.1"
                  max="200"
                  step="0.5"
                  className="form-control"
                  value={radiusKm}
                  onChange={this._handleRadiusChange}
                  aria-label="Delivery radius in kilometres"
                />
                <div className="input-group-append">
                  <span className="input-group-text">km</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="card-body p-0 position-relative">
          {this.props.loading && (
            <div
              className="d-flex align-items-center justify-content-center position-absolute w-100 h-100"
              style={{ zIndex: 500, background: "rgba(255, 255, 255, 0.75)" }}
            >
              <span className="spinner-border text-primary mr-2" />
              Loading vendor locations...
            </div>
          )}

          {!this.props.loading && countyLoading && (
            <div className="vendor-map-boundary-status">
              Loading county boundaries...
            </div>
          )}

          {countyError && (
            <div className="vendor-map-boundary-status text-danger">
              County boundaries could not be loaded.
            </div>
          )}

          <div
            ref={this.mapContainer}
            aria-label="Map of vendor locations and delivery coverage in Ireland"
            style={{ height: 560, width: "100%" }}
          />
        </div>
      </div>
    );
  }
}

VendorPresenceMap.defaultProps = {
  locations: [],
  loading: false,
  defaultRadiusKm: 5,
  countyGeoJsonUrl: COUNTY_GEOJSON_URL,
};
